import { useLayoutEffect, useRef, useState } from 'react';

/**
 * One sidebar section, collapsed into a dropdown.
 *
 * The rail used to render every section's full option list at once — Degree
 * Level, Duration, Quick filters and English proficiency all open on the page
 * permanently, whatever else was picked. That reads as "static": nothing to
 * click, no sense of what changed, and a lot of vertical space spent on
 * options nobody is looking at right now. This wraps the SAME content (still
 * `FacetList`, still `TuitionRange`, still whatever a section renders) in the
 * same `<details class="multi-select">` shell `SearchableSelect` already uses
 * for Study Destination / Student Nationality / Course intake — same chevron,
 * same focus ring, same click-away and Escape handling, same mobile CSS. One
 * shell, one behaviour, everywhere a rail control opens.
 *
 * Unlike `SearchableSelect` there is no search box: these lists are short
 * enough (5 degree levels, 5 duration bands, a handful of English tests, up to
 * ~20 course types) that a filter box would be one more thing to ignore.
 *
 * `summary` is plain text — "Any", "2 selected", "Undergraduate" — computed by
 * the caller, because only the caller knows how to describe its own selection
 * ("2 selected" for checkboxes, "£100–£500" for a range, "12–18 months" for a
 * bucket).
 *
 * --- why the menu is positioned in JS, not by the CSS the shell already has -
 *
 * `.multi-menu`'s normal `position:absolute` works for Study Destination and
 * the rest of the top search bar because nothing between them and the page
 * edge clips overflow. The rail is different: `aside` scrolls internally
 * (`overflow-y:auto`, so a tall option list doesn't push the whole page down),
 * and an absolutely-positioned descendant is clipped by the nearest ancestor
 * whose overflow is not `visible` — that's the aside itself, not the viewport.
 * The menu opened squashed against the aside's own edge, or was cut off
 * outright, depending on where the row sat when it opened - the reported "the
 * dropdown looks congested when I open it" bug. Measuring the row's own
 * position and rendering the menu `position:fixed` at those viewport
 * coordinates escapes that clipping entirely: a fixed element is placed
 * relative to the viewport regardless of which scrollable box its DOM parent
 * sits inside, the same technique Popper/Floating UI use for exactly this.
 */
export function DropdownFacet({ id, label, summary, active, disabled, disabledNote, children }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const ref = useRef(null);

  // Layout effect, not a plain effect: the position must be known BEFORE the
  // browser paints the newly-opened menu, or it flashes at its CSS default
  // (top-left of the row) for one frame before snapping to the real spot.
  useLayoutEffect(() => {
    if (!open) { setMenuStyle(null); return undefined; }

    const reposition = () => {
      const row = ref.current?.querySelector(':scope > summary');
      if (!row) return;
      const r = row.getBoundingClientRect();
      const gap = 4;
      // Keep the panel on-screen if the row is near the bottom of the
      // viewport: open upward rather than run off the bottom edge.
      const spaceBelow = window.innerHeight - r.bottom;
      const openUpward = spaceBelow < 200 && r.top > spaceBelow;
      setMenuStyle({
        position: 'fixed',
        left: Math.round(r.left),
        // Exact control width, so the menu never spills past its own field
        // (long option labels ellipsise inside instead).
        width: Math.round(r.width),
        // The shared `.multi-select`/`.multi-menu` shell (SearchableSelect)
        // sets `top: calc(100% + 4px)` via its own class
        // rule for its normal absolute-positioned case. An inline style here
        // wins over that rule for whichever property it sets, but NOT for one
        // it leaves alone — so opening upward (setting only `bottom`) left the
        // class's `top` still in effect, and a fixed box with both `top` and
        // `bottom` resolves to `top`, undoing the whole point of opening
        // upward. Setting both explicitly, one of them to 'auto', is what
        // actually overrides the class rule in both directions.
        top: openUpward ? 'auto' : Math.round(r.bottom + gap),
        bottom: openUpward ? Math.round(window.innerHeight - r.top + gap) : 'auto',
      });
    };

    reposition();
    // `scroll` does not bubble, but the capture phase still sees it fire on
    // any descendant — including the aside's own internal scrollbar — so one
    // listener on the window covers every scroll container on the page.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      ref.current?.querySelector('summary')?.focus();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A locked section (Course type / Course intake before a destination is
  // chosen) still shows its explanatory note when opened, exactly as it did
  // as a static block — it just isn't open by default any more.
  return (
    <div className="fld facet-fld">
      {/* Label sits ABOVE the control, matching the primary search-bar fields
          (Study destination / University / …) rather than the old inline
          "Degree level  Any ⌄" layout the sidebar used. */}
      <label>{label}</label>
      <details
        className="multi-select facet-dropdown"
        id={id}
        ref={ref}
        open={open}
        onToggle={(e) => setOpen(e.currentTarget.open)}
      >
        <summary className={active ? 'sel' : undefined} aria-disabled={disabled || undefined}>
          <span className="fd-value">{summary}</span>
        </summary>
        <div className="multi-menu facet-menu" style={menuStyle ?? undefined}>
          {disabled ? <p className="facet-locked-note">{disabledNote}</p> : children}
        </div>
      </details>
    </div>
  );
}
